import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

const PieClockContainer = styled.div`
  flex-direction: row;
  display: flex;
`;

const width = 90;
const height = 90;
const borderWidth = 8;

interface DrawProps {
  ctx: CanvasRenderingContext2D;
  centerX: number;
  centerY: number;
  radius: number;
  progress: number;
  borderColorForeground: string;
  borderColorBackground: string;
}
const drawBorderForeground = ({
  ctx,
  radius,
  centerX,
  centerY,
  progress,
  borderColorForeground
}: DrawProps) => {
  ctx.strokeStyle = borderColorForeground;
  ctx.lineWidth = borderWidth;
  ctx.beginPath();
  ctx.arc(
    centerX,
    centerY,
    radius,
    Math.PI * -0.5,
    Math.PI * (progress * 0.01 * 2 - 0.5)
  );
  ctx.stroke();
};

const drawBorderBackground = ({
  ctx,
  radius,
  centerX,
  centerY,
  borderColorBackground
}: DrawProps) => {
  ctx.strokeStyle = borderColorBackground;
  ctx.lineWidth = borderWidth;

  ctx.scale(2, 2);
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, Math.PI * -0.5, Math.PI * 1.5);
  ctx.stroke();
};

export interface PieClockProps {
  progress: number;
  borderColorForeground: string;
  borderColorBackground: string;
}
export const PieClock = ({
  progress,
  borderColorBackground,
  borderColorForeground
}: PieClockProps) => {
  const canvas = useRef<HTMLCanvasElement>(null);

  const draw = useCallback(
    (canvas: HTMLCanvasElement) => {
      canvas.width = (width + borderWidth) * 2;
      canvas.height = (height + borderWidth) * 2;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${width}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      const radius = canvas.offsetWidth / 2;
      const centerX = (canvas.offsetWidth + borderWidth) / 2;
      const centerY = (canvas.offsetHeight + borderWidth) / 2;

      drawBorderBackground({
        ctx,
        centerX,
        centerY,
        radius,
        progress,
        borderColorForeground,
        borderColorBackground
      });
      drawBorderForeground({
        ctx,
        centerX,
        centerY,
        radius,
        progress,
        borderColorForeground,
        borderColorBackground
      });
    },
    [borderColorBackground, borderColorForeground, progress]
  );

  useEffect(() => {
    const element = canvas.current;

    if (element) {
      draw(element);
    }
  }, [draw, progress]);

  return (
    <PieClockContainer data-testid="pie-clock">
      <canvas ref={canvas} width={width} height={height} />
    </PieClockContainer>
  );
};

export default PieClock;
